import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import ReportFilters from '../components/reports/ReportFilters';
import ReportTable from '../components/reports/ReportTable';
import { generateReport } from '../services/reportsApi';
import { openReportPrintWindow } from '../utils/reportExport';
import { toLocalISODate } from '../utils/dateUtils';

export default function Reports() {
  const { vehicles } = useApp();
  const [reportKey, setReportKey] = useState('vehicle-trip-detail');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toLocalISODate(d);
  });
  const [to, setTo] = useState(() => toLocalISODate());
  const [vehicleId, setVehicleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);

  const rangeInverted = from && to && from > to;

  const handleReportKeyChange = (nextKey) => {
    setReportKey(nextKey);
    setReport(null);
    // Clear vehicle when switching away from vehicle-required reports
    // so optional filters don't accidentally carry over.
    if (nextKey !== 'vehicle-trip-detail' && nextKey !== 'fuel-consumption') {
      setVehicleId('');
    }
  };

  const fetchReport = async () => {
    const data = await generateReport(reportKey, { from, to, vehicleId });
    setReport(data);
    return data;
  };

  const onGenerate = async () => {
    setLoading(true);
    try {
      await fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Report failed');
    } finally {
      setLoading(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const data = report || (await fetchReport());
      openReportPrintWindow(data);
      toast.success('Print dialog opened — choose Save as PDF');
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo(() => report?.rows || [], [report]);
  const summary = report?.summary || null;
  const title = report?.meta?.title || report?.title || reportKey;

  const subtitleBits = [
    `${rows.length} row${rows.length === 1 ? '' : 's'}`,
    summary?.totalActualKm != null ? `Total km ${summary.totalActualKm}` : null,
    summary?.totalLiters != null ? `Liters ${summary.totalLiters}` : null,
    summary?.totalCostRs != null ? `Rs ${summary.totalCostRs}` : null,
  ].filter(Boolean);

  return (
    <div>
      <div className="page-header">
        <h2>Reports</h2>
        <p>
          Vehicle trip detail, fleet total summary, fuel-wise consumption, driver summary,
          and flagged trips — export any report as PDF.
        </p>
      </div>

      <ReportFilters
        reportKey={reportKey}
        from={from}
        to={to}
        vehicleId={vehicleId}
        vehicles={vehicles}
        loading={loading}
        exporting={exporting}
        rangeInverted={rangeInverted}
        onChangeReportKey={handleReportKeyChange}
        onChangeFrom={setFrom}
        onChangeTo={setTo}
        onChangeVehicleId={setVehicleId}
        onGenerate={onGenerate}
        onExport={onExport}
      />

      {report && (
        <>
          <p className="text-muted" style={{ marginBottom: 12 }}>
            {title} · {subtitleBits.join(' · ')}
          </p>
          <ReportTable
            reportKey={report?.meta?.key || reportKey}
            columns={report.columns || []}
            rows={rows}
            summary={summary}
            vehicleSummary={report.vehicleSummary || null}
          />
        </>
      )}
    </div>
  );
}
